import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string | null;
  canonical?: string;
  type?: 'website' | 'article' | 'product';
  jsonLd?: Record<string, unknown>;
  noIndex?: boolean;
}

/**
 * 🔍 مكوّن SEO ديناميكي
 *
 * يحدّث وسوم <head> بدون react-helmet (لتجنب dependency إضافية).
 * - title
 * - meta description
 * - meta keywords
 * - canonical
 * - Open Graph (og:*)
 * - Twitter Cards
 * - JSON-LD structured data
 *
 * الاستخدام:
 *   <SEO
 *     title="فلل للبيع في الرياض"
 *     description="..."
 *     keywords="فلل، الرياض، للبيع"
 *     image="/og.jpg"
 *     jsonLd={{ '@type': 'RealEstateListing', ... }}
 *   />
 */
const SITE_NAME = 'شركة صورة العقارية';
const SITE_URL = 'https://img.sa';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_DESCRIPTION = 'منصة شركة صورة العقارية للعقارات والمزادات الإلكترونية في المملكة العربية السعودية.';

function setMeta(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(id: string, data: Record<string, unknown>) {
  let el = document.querySelector<HTMLScriptElement>(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo-id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify({ '@context': 'https://schema.org', ...data });
}

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  image = DEFAULT_IMAGE,
  canonical,
  type = 'website',
  jsonLd,
  noIndex = false,
}: SEOProps) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — العقارات والمزادات في السعودية`;
    document.title = fullTitle;

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large');

    // Open Graph
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', type, true);
    setMeta('og:url', canonical ?? window.location.href, true);
    if (image) setMeta('og:image', image, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:locale', 'ar_SA', true);

    // Twitter
    setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (image) setMeta('twitter:image', image);

    // Canonical
    setLink('canonical', canonical ?? window.location.href);

    // JSON-LD
    if (jsonLd) setJsonLd('page', jsonLd);
  }, [title, description, keywords, image, canonical, type, jsonLd, noIndex]);

  return null;
}
